import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2, Trash2, Download, ThumbsUp, ThumbsDown, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useMonthlySummary } from '@/hooks/useMonthlySummary';
import { useGoals } from '@/hooks/useGoals';
import { useDebts } from '@/hooks/useDebts';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { useAIConsent } from '@/hooks/useAIConsent';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import ReactMarkdown from 'react-markdown';
import { AIConsentModal } from './AIConsentModal';

interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  feedback?: 'positive' | 'negative' | null;
}

const SUGGESTED_QUESTIONS = [
  "¿Cómo puedo mejorar mi FOCO Score?",
  "Analiza mis gastos de este mes",
  "Dame consejos para ahorrar más",
  "¿Cómo puedo pagar mis deudas más rápido?",
];

export function FinancialAdvisorChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isUserScrolledUp = useRef(false);
  const { hasConsent, isLoading: isConsentLoading, grantConsent } = useAIConsent();

  const { user } = useAuth();
  const now = new Date();
  const { summary } = useMonthlySummary(now.getFullYear(), now.getMonth() + 1);
  const { goals } = useGoals();
  const { debts } = useDebts();
  const { profile } = useProfile();

  const activeGoals = goals?.filter(g => g.status === 'active').length || 0;
  const activeDebts = debts?.filter(d => d.status === 'active').length || 0;

  // Load chat history from database
  const loadChatHistory = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoadingHistory(true);
    try {
      // Load messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('chat_messages')
        .select('id, role, content')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;
      
      if (messagesData && messagesData.length > 0) {
        // Load feedback for these messages
        const messageIds = messagesData.filter(m => m.role === 'assistant').map(m => m.id);
        
        let feedbackMap: Record<string, 'positive' | 'negative'> = {};
        
        if (messageIds.length > 0) {
          const { data: feedbackData } = await supabase
            .from('chat_feedback')
            .select('message_id, feedback')
            .in('message_id', messageIds);
          
          if (feedbackData) {
            feedbackMap = feedbackData.reduce((acc, f) => {
              acc[f.message_id] = f.feedback as 'positive' | 'negative';
              return acc;
            }, {} as Record<string, 'positive' | 'negative'>);
          }
        }
        
        setMessages(messagesData.map(m => ({ 
          id: m.id, 
          role: m.role as 'user' | 'assistant', 
          content: m.content,
          feedback: feedbackMap[m.id] || null
        })));
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user?.id]);

  // Handle feedback submission
  const handleFeedback = async (messageId: string, feedback: 'positive' | 'negative') => {
    if (!user?.id || !messageId) return;

    try {
      // Check if feedback already exists
      const { data: existing } = await supabase
        .from('chat_feedback')
        .select('id, feedback')
        .eq('message_id', messageId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        if (existing.feedback === feedback) {
          // Remove feedback if clicking same button
          await supabase
            .from('chat_feedback')
            .delete()
            .eq('id', existing.id);
          
          setMessages(prev => prev.map(m => 
            m.id === messageId ? { ...m, feedback: null } : m
          ));
        } else {
          // Update feedback
          await supabase
            .from('chat_feedback')
            .update({ feedback })
            .eq('id', existing.id);
          
          setMessages(prev => prev.map(m => 
            m.id === messageId ? { ...m, feedback } : m
          ));
        }
      } else {
        // Insert new feedback
        await supabase
          .from('chat_feedback')
          .insert({ message_id: messageId, user_id: user.id, feedback });
        
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, feedback } : m
        ));
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el feedback',
        variant: 'destructive',
      });
    }
  };

  // Load history when chat opens
  useEffect(() => {
    if (isOpen && user?.id && messages.length === 0) {
      loadChatHistory();
    }
  }, [isOpen, user?.id, loadChatHistory, messages.length]);

  const scrollToBottom = useCallback(() => {
    if (!isUserScrolledUp.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const threshold = 60;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    isUserScrolledUp.current = distanceFromBottom > threshold;
  }, []);

  useEffect(() => {
    // Small delay to ensure content is rendered before scrolling
    const timer = setTimeout(scrollToBottom, 50);
    return () => clearTimeout(timer);
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Save message to database
  const saveMessage = async (role: 'user' | 'assistant', content: string): Promise<string | null> => {
    if (!user?.id || !content.trim()) return null;
    
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({ user_id: user.id, role, content })
        .select('id')
        .single();

      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error('Error saving message:', error);
      return null;
    }
  };

  const getFinancialContext = () => ({
    focoScore: summary?.foco_score,
    income: summary?.total_income,
    fixedExpenses: summary?.total_fixed_expenses,
    variableExpenses: summary?.total_variable_expenses,
    savings: (summary?.total_savings || 0) + (summary?.total_investments || 0),
    debtPayments: summary?.total_debt_payments,
    cashFlow: summary?.real_cash_flow,
    savingsRate: summary?.savings_rate ? Math.round(summary.savings_rate * 100) : 0,
    debtRatio: summary?.debt_ratio ? Math.round(summary.debt_ratio * 100) : 0,
    fixedExpenseRatio: summary?.fixed_expense_ratio ? Math.round(summary.fixed_expense_ratio * 100) : 0,
    variableExpenseRatio: summary?.variable_expense_ratio ? Math.round(summary.variable_expense_ratio * 100) : 0,
    activeGoals,
    activeDebts,
    currency: profile?.default_currency || 'CRC',
  });

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    // Check AI consent before sending
    if (!hasConsent) {
      setPendingMessage(messageText);
      setShowConsentModal(true);
      return;
    }

    const userMessage: Message = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    isUserScrolledUp.current = false;

    // Save user message to database
    const userMsgId = await saveMessage('user', messageText);
    if (userMsgId) {
      setMessages(prev => prev.map((m, i) => 
        i === prev.length - 1 ? { ...m, id: userMsgId } : m
      ));
    }

    let assistantContent = '';

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No estás autenticado. Inicia sesión para usar el asesor.');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financial-advisor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          messages: [...messages, userMessage].map(m => ({ role: m.role, content: m.content })),
          financialContext: getFinancialContext(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al conectar con el asistente');
      }

      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';

      // Add empty assistant message to update
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: assistantContent };
                return updated;
              });
              scrollToBottom();
            }
          } catch {
            // Partial JSON, wait for more data
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Save assistant message to database after streaming completes
      if (assistantContent.trim()) {
        const assistantMsgId = await saveMessage('assistant', assistantContent);
        if (assistantMsgId) {
          setMessages(prev => prev.map((m, i) => 
            i === prev.length - 1 ? { ...m, id: assistantMsgId } : m
          ));
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'No se pudo conectar con el asistente',
        variant: 'destructive',
      });
      // Remove the empty assistant message if error
      setMessages(prev => prev.filter(m => m.content !== ''));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleConsentAccept = async () => {
    const ok = await grantConsent();
    setShowConsentModal(false);
    if (ok && pendingMessage) {
      sendMessage(pendingMessage);
      setPendingMessage(null);
    }
  };

  const handleConsentDecline = () => {
    setShowConsentModal(false);
    setPendingMessage(null);
  };

  const exportToPDF = () => {
    if (messages.length === 0) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPosition = 20;

    // Header
    doc.setFontSize(18);
    doc.setTextColor(31, 47, 74); // Navy blue
    doc.text('Asesor Financiero IA', margin, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('FOCO AI System', margin, yPosition);
    yPosition += 6;
    doc.text(`Exportado: ${new Date().toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, margin, yPosition);
    yPosition += 15;

    // Line separator
    doc.setDrawColor(46, 134, 193);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Messages
    doc.setFontSize(11);
    
    messages.forEach((message) => {
      // Check if we need a new page
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }

      // Role label
      doc.setTextColor(message.role === 'user' ? 31 : 100, message.role === 'user' ? 47 : 100, message.role === 'user' ? 74 : 100);
      doc.setFont('helvetica', 'bold');
      doc.text(message.role === 'user' ? 'Tú:' : 'Asesor:', margin, yPosition);
      yPosition += 6;

      // Message content
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(message.content, maxWidth);
      
      lines.forEach((line: string) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });
      
      yPosition += 8;
    });

    doc.save(`chat-financiero-${new Date().toISOString().split('T')[0]}.pdf`);
    toast({
      title: 'PDF exportado',
      description: 'El historial se ha descargado correctamente',
    });
  };

  const clearChat = async () => {
    if (!user?.id) return;
    
    try {
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;
      
      setMessages([]);
      toast({
        title: 'Historial borrado',
        description: 'Se ha eliminado el historial de conversaciones',
      });
    } catch (error) {
      console.error('Error clearing chat:', error);
      toast({
        title: 'Error',
        description: 'No se pudo borrar el historial',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      {/* Floating Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg touch-manipulation',
          'bg-primary hover:bg-primary/90 text-primary-foreground',
          'transition-transform hover:scale-105 active:scale-95',
          isOpen && 'hidden'
        )}
      >
        <Sparkles className="h-6 w-6" />
      </Button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:inset-auto lg:bottom-6 lg:right-6 lg:w-[420px] lg:h-[600px] lg:max-h-[80vh]">
          {/* Backdrop for mobile */}
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm lg:hidden"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Chat Container */}
          <div className="relative h-full flex flex-col bg-card border rounded-none lg:rounded-2xl shadow-2xl overflow-hidden safe-area-inset">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-primary/5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Asesor Financiero IA</h3>
                  <p className="text-xs text-muted-foreground">FOCO AI System</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <>
                    <Button variant="ghost" size="icon" onClick={exportToPDF} className="h-8 w-8" title="Exportar a PDF">
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={clearChat} className="h-8 w-8" title="Borrar historial">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div ref={messagesContainerRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto p-4">
              {/* No-consent banner */}
              {hasConsent === false && !isConsentLoading && messages.length === 0 && (
                <div className="text-center py-8 space-y-4">
                  <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto">
                    <ShieldAlert className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">IA no activada</h4>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      Para usar el asesor IA necesitas aceptar el uso de IA con tus datos financieros. Puedes activar esta opción en Configuración o escribir un mensaje para comenzar.
                    </p>
                  </div>
                </div>
              )}
              {(hasConsent !== false || messages.length > 0) && isLoadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (hasConsent !== false || messages.length > 0) && messages.length === 0 ? (
                <div className="space-y-4">
                  <div className="text-center py-6">
                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="h-8 w-8 text-primary" />
                    </div>
                    <h4 className="font-semibold mb-2">¡Hola! Soy tu asesor financiero</h4>
                    <p className="text-sm text-muted-foreground">
                      Puedo analizar tus finanzas y darte recomendaciones personalizadas.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Preguntas sugeridas
                    </p>
                    {SUGGESTED_QUESTIONS.map((question, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(question)}
                        className="w-full text-left p-3 rounded-lg border bg-background hover:bg-muted/50 text-sm transition-colors touch-manipulation"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, i) => (
                    <div
                      key={message.id || i}
                      className={cn(
                        'flex flex-col',
                        message.role === 'user' ? 'items-end' : 'items-start'
                      )}
                    >
                      <div
                        className={cn(
                          'max-w-[85%] rounded-2xl px-4 py-3 text-sm',
                          message.role === 'user'
                            ? 'bg-foreground text-background rounded-br-md'
                            : 'bg-muted text-foreground rounded-bl-md'
                        )}
                      >
                        {message.role === 'assistant' ? (
                          <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-strong:text-foreground">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        )}
                      </div>
                      {/* Feedback buttons for assistant messages */}
                      {message.role === 'assistant' && message.id && message.content && (
                        <div className="flex items-center gap-1 mt-1 ml-1">
                          <button
                            onClick={() => handleFeedback(message.id!, 'positive')}
                            className={cn(
                              'p-1.5 rounded-full transition-colors',
                              message.feedback === 'positive'
                                ? 'bg-success/20 text-success'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                            title="Respuesta útil"
                          >
                            <ThumbsUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleFeedback(message.id!, 'negative')}
                            className={cn(
                              'p-1.5 rounded-full transition-colors',
                              message.feedback === 'negative'
                                ? 'bg-destructive/20 text-destructive'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                            title="Respuesta no útil"
                          >
                            <ThumbsDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && messages[messages.length - 1]?.content === '' && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t bg-background">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe tu pregunta..."
                  disabled={isLoading}
                  className="flex-1 h-11 text-base"
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  disabled={!input.trim() || isLoading}
                  className="h-11 w-11 touch-manipulation"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Consent Modal */}
      <AIConsentModal
        open={showConsentModal}
        onAccept={handleConsentAccept}
        onDecline={handleConsentDecline}
      />
    </>
  );
}
